import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import eventAPI from '../services/eventAPI';
import Navbar from '../components/Navbar';
import EventModal from '../components/EventModal';
import EventDetailsModal from '../components/EventDetailsModal';
import Footer from '../components/Footer';
import './Calendar.css';

function Calendar() {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadEvents();
  }, [currentDate]);

  // Auto-refresh events every minute
  useEffect(() => {
    const interval = setInterval(() => {
      loadEvents();
    }, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Load current user
      const userData = await authAPI.getCurrentUser();
      setCurrentUser(userData);

      // Load events
      await loadEvents();
    } catch (error) {
      console.error('Error loading calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEvents = async () => {
    try {
      const response = await eventAPI.getEvents();
      setEvents(response.events || []);
    } catch (error) {
      console.error('Error loading events:', error);
    }
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    // Adjust for Monday as first day of week (0=Monday, 6=Sunday)
    const startingDayOfWeek = (firstDay.getDay() + 6) % 7;

    const days = [];

    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const getEventsForDate = (date) => {
    if (!date) return [];

    return events.filter(event => {
      // Show confirmed/scheduled events with finalized datetime
      if (event.finalized_datetime) {
        const eventDate = new Date(event.finalized_datetime);
        return eventDate.toDateString() === date.toDateString();
      }

      // Show planning events on their voting deadline
      if (event.status === 'planning' && event.voting_deadline) {
        const deadlineDate = new Date(event.voting_deadline);
        return deadlineDate.toDateString() === date.toDateString();
      }

      return false;
    });
  };

  const handlePreviousMonth = () => {
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
    setCurrentDate(newDate);
  };

  const handleNextMonth = () => {
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
    setCurrentDate(newDate);
  };

  const handleToday = () => {
    const today = new Date();
    setCurrentDate(today);
  };

  const handleCreateEvent = () => {
    setSelectedDate(null);
    setShowCreateModal(true);
  };

  const handleDayClick = (date) => {
    if (date) {
      setSelectedDate(date);
      setShowCreateModal(true);
    }
  };

  const handleEventClick = (event, e) => {
    e.stopPropagation();

    // Check if event should open recap page
    const isFinalized = event.event_type === 'direct' ||
                        event.status === 'scheduled' ||
                        event.status === 'confirmed';

    const eventDate = event.finalized_datetime ? new Date(event.finalized_datetime) : null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const isPastOrToday = eventDate && eventDate <= new Date(); // Compare with current time

    if (isFinalized && isPastOrToday) {
      // Navigate to recap page
      navigate(`/event/${event.id}/recap`);
    } else {
      // Open modal
      setSelectedEventId(event.id);
    }
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setSelectedDate(null);
  };

  const handleCloseDetailsModal = () => {
    setSelectedEventId(null);
    loadEvents(); // Reload events after closing
  };

  const handleEventCreated = () => {
    loadEvents();
    setShowCreateModal(false);
  };

  const monthNames = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
  ];

  const dayNames = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

  const days = getDaysInMonth();

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="calendar-container">
          <div className="retro-loading">
            <div className="loading-spinner"></div>
            <p>LOADING...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="calendar-container">
        <div className="calendar-header">
          <div className="calendar-header-content">
            <h1>Familienkalender</h1>
            <div className="calendar-controls">
              <button onClick={handlePreviousMonth} className="calendar-nav-button">
                &lt;
              </button>
              <button onClick={handleToday} className="calendar-today-button">
                Heute
              </button>
              <button onClick={handleNextMonth} className="calendar-nav-button">
                &gt;
              </button>
              <button onClick={handleCreateEvent} className="calendar-create-button">
                + EVENT
              </button>
            </div>
          </div>
          <div className="calendar-month-display">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </div>
        </div>

      <div className="calendar-grid-container">
        <div className="calendar-weekday-headers">
          {dayNames.map(day => (
            <div key={day} className="calendar-weekday-header">
              {day}
            </div>
          ))}
        </div>

        <div className="calendar-grid">
          {days.map((date, index) => {
            const isToday = date && date.toDateString() === new Date().toDateString();
            const dayEvents = date ? getEventsForDate(date) : [];

            return (
              <div
                key={index}
                className={`calendar-day ${!date ? 'empty' : ''} ${isToday ? 'today' : ''}`}
                onClick={() => handleDayClick(date)}
                style={{ cursor: date ? 'pointer' : 'default' }}
              >
                {date && (
                  <>
                    <div className="calendar-day-number">
                      {date.getDate()}
                    </div>
                    <div className="calendar-day-events">
                      {dayEvents.slice(0, 3).map(event => {
                        const colorMap = {
                          purple: '#9d5cff',
                          pink: '#ec4899',
                          blue: '#3b82f6',
                          green: '#10b981',
                          orange: '#f59e0b',
                          red: '#ef4444'
                        };
                        const backgroundColor = colorMap[event.color] || event.color;

                        const isDirect = event.event_type === 'direct';
                        const isPlanning = event.event_type === 'planning';
                        const isFinalized = event.status === 'scheduled' || event.status === 'confirmed';
                        const isProposed = event.status === 'planning';

                        let eventIcon = '';
                        let eventClass = '';

                        if (isProposed) {
                          // Still voting/proposed
                          eventIcon = '[?] ';
                          eventClass = 'proposed-event';
                        } else if (isDirect) {
                          // Direct event (always final)
                          eventIcon = '[#] ';
                          eventClass = 'direct-event';
                        } else if (isPlanning && isFinalized) {
                          // Finalized planning event
                          eventIcon = '[✓] ';
                          eventClass = 'finalized-event';
                        }

                        return (
                          <div
                            key={event.id}
                            className={`calendar-event-chip ${eventClass}`}
                            style={{ backgroundColor }}
                            onClick={(e) => handleEventClick(event, e)}
                          >
                            <span className="event-icon">{eventIcon}</span>
                            <span className="event-chip-title">{event.title}</span>
                          </div>
                        );
                      })}
                      {dayEvents.length > 3 && (
                        <div className="calendar-event-more">
                          +{dayEvents.length - 3} mehr
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Create Event Modal */}
      {showCreateModal && (
        <EventModal
          onClose={handleCloseCreateModal}
          onEventCreated={handleEventCreated}
          currentUser={currentUser}
          initialDate={selectedDate}
        />
      )}

      {/* Event Details Modal */}
      {selectedEventId && (
        <EventDetailsModal
          eventId={selectedEventId}
          onClose={handleCloseDetailsModal}
          onEventUpdated={loadEvents}
          currentUserId={currentUser?.id}
        />
      )}

      <Footer />
      </div>
    </>
  );
}

export default Calendar;
